// Trang Khoe Thành Tích Tháng — STT 17 (URL /khoe-thanh-tich-thang).
// Số liệu doanh số lấy từ MonthlySalesAPI.getSummary → BE /orders/monthly-summary.
//   sales.soLe = đơn LẺ (orders.type=1)   ← KH lẻ
//   sales.soSi = đơn SỈ (orders.type=2)   ← đại lý / bán buôn
// Nếu thấy đơn lẻ / đơn sỉ ở UI bị "đảo", check trước getMonthlyOrderSummary
// trong mlm-core-service/src/modules/order/order.service.ts (đó là thủ phạm cũ).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SystemHeader from '../SystemHeader';
import {
  DailyReportAPI, ReelsAutoCheckAPI, ReelTrendingAPI,
  LivestreamAPI, TeamMeetingAPI, MarketAPI, SpaMarketAPI,
  MonthlySalesAPI, ThongDiepAPI, YearlySalesAPI, MocDanhHieuAPI,
} from '../../services/feature-api';
import { ChevronLeft, ChevronRight, Shuffle, List, X, Trophy, Lock, Check } from 'lucide-react';
import { cdnThumb, retryOriginalOnce } from '../../utils/CdnImage';

// Màu theo tháng (1-12)
const MONTH_PALETTE = [
  '#F59E0B','#EC4899','#8B5CF6','#06B6D4','#10B981',
  '#EF4444','#6366F1','#F97316','#14B8A6','#A855F7','#3B82F6','#22C55E',
];

const fmtVNDFull = (n) => `${Number(n || 0).toLocaleString('vi-VN')} đ`;
const fmtVNDShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}tỷ`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}tr`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
};
const fmtTime = (val) => { try { return new Date(val).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const toDateStr = (d) => { if (!d) return ''; const dt = typeof d === 'string' ? new Date(d) : d; if (isNaN(dt.getTime())) return ''; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`; };
const itemDateStr = (item) => { if (!item) return ''; if (item.date) return typeof item.date === 'string' && item.date.length >= 10 ? item.date.slice(0,10) : toDateStr(item.date); if (item.time) return toDateStr(item.time); if (item.createdAt) return toDateStr(item.createdAt); return ''; };
const itemInMonth = (item, month, year) => { const d = itemDateStr(item); if (!d) return false; const [y,m] = d.split('-'); return Number(m) === month && Number(y) === year; };

// Gradient border image. Khi src lỗi (thumb FB hết hạn) → KHÔNG ẩn,
// chuyển sang placeholder gradient để khung không vỡ layout grid.
const GlowPhoto = ({ src, accent, className = 'w-full object-cover rounded-xl' }) => {
  if (!src) return null;
  return (
    <div style={{ padding: 2, background: `linear-gradient(135deg, ${accent}, #9333ea, ${accent}88)`, borderRadius: 14, flexShrink: 0 }}>
      <img src={cdnThumb(src)} alt="" className={className} loading="lazy" decoding="async" style={{ borderRadius: 12, display: 'block' }}
        onError={(e) => {
          if (retryOriginalOnce(e.target)) return; // thumb hỏng → thử ảnh gốc 1 lần
          const fallback = e.target.parentElement;
          fallback.innerHTML = `<div style="width:100%;height:100%;border-radius:12px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,${accent}33,#9333ea33,${accent}11);font-size:24px;">📸</div>`;
        }} />
    </div>
  );
};

// Seeded random shuffle theo ngày — cùng input + cùng ngày = cùng output.
// Mục đích: mỗi ngày user mở app thấy thumb khác nhau, nhưng trong ngày stable.
const shuffleByDay = (arr) => {
  const seedDay = Math.floor(Date.now() / 86400000); // ngày-tính-từ-1970
  const out = [...arr];
  let s = seedDay;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// Block container with header label
const Block = ({ accent, label, children }) => (
  <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: '#0d1f35', boxShadow: `0 0 0 1px ${accent}44, 0 4px 20px #00000040` }}>
    <div className="px-4 py-2 flex items-center gap-2" style={{ background: `${accent}22`, borderBottom: `1px solid ${accent}44` }}>
      <div className="w-1.5 h-4 rounded-full flex-shrink-0" style={{ background: accent }} />
      <span className="text-xs font-black uppercase tracking-widest" style={{ color: accent }}>{label}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Stat tile: big number + label
// 1 block RIÊNG cho mỗi loại đơn (lẻ/sỉ) — mỗi block 1 màu + icon riêng để phân biệt NHANH.
// value = số SẢN PHẨM (qty), money = tổng giá trị tiền = Σ(qty × đơn giá) — hiện số trần, KHÔNG đuôi "đ".
const SoldBlock = ({ color, icon, label, value, money }) => (
  <div className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(165deg, ${color}2e, ${color}0d)`, border: `1.5px solid ${color}80`, boxShadow: `0 4px 18px ${color}30` }}>
    <div className="flex items-center justify-center gap-1.5 py-2" style={{ background: `${color}33`, borderBottom: `1px solid ${color}55` }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span className="text-xs font-black uppercase tracking-wider" style={{ color }}>{label}</span>
    </div>
    <div className="flex items-end justify-center gap-1 pt-3 pb-1.5">
      <span className="font-black text-white leading-none" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 24px ${color}` }}>
        {Number(value || 0).toLocaleString('vi-VN')}
      </span>
      <span className="text-sm font-black mb-1.5" style={{ color }}>sp</span>
    </div>
    <div className="mx-3 mb-3 rounded-lg py-1 text-center" style={{ background: `${color}26`, border: `1px solid ${color}45` }}>
      <span className="font-black tracking-wide" style={{ fontSize: 15, color }}>{Number(money || 0).toLocaleString('vi-VN')}</span>
    </div>
  </div>
);

// Format mốc tiền GỌN cho thang danh hiệu (tránh ".0": 100tr, 500tr, 1 tỷ, 2 tỷ).
const fmtMoc = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000_000) { const t = v / 1_000_000_000; return `${Number.isInteger(t) ? t : t.toFixed(1)} tỷ`; }
  if (v >= 1_000_000) { const t = v / 1_000_000; return `${Number.isInteger(t) ? t : t.toFixed(0)}tr`; }
  return v.toLocaleString('vi-VN');
};
const fmtRange = (min, max) => (max == null ? `≥ ${fmtMoc(min)}` : `${Number(min) === 0 ? '0' : fmtMoc(min)} – ${fmtMoc(max)}`);

// Xác định danh hiệu theo tổng doanh thu năm (orders lẻ+sỉ). Tier đỉnh doanhThuMax=null → ≥ min là đạt.
// progress = % trong tier hiện tại tới mốc kế (= doanhThuMax). Tier đỉnh = 100%.
const computeBadge = (ladder, total) => {
  if (!ladder?.length) return null;
  const sorted = [...ladder].sort((a, b) => Number(a.doanhThuMin) - Number(b.doanhThuMin));
  let current = sorted[0];
  for (const t of sorted) if (total >= Number(t.doanhThuMin)) current = t;
  const idx = sorted.findIndex((t) => t.id === current.id);
  const next = sorted[idx + 1] || null;
  const min = Number(current.doanhThuMin);
  const max = current.doanhThuMax == null ? null : Number(current.doanhThuMax);
  let progress = 100;
  if (max != null && max > min) progress = Math.min(100, Math.max(0, ((total - min) / (max - min)) * 100));
  return { sorted, current, next, idx, progress, min, max };
};

// Card danh hiệu — thay cho khối "Tổng doanh thu". KHÔNG hiện tiền (nhạy cảm),
// chỉ danh hiệu + tiến độ + số đơn lẻ/sỉ cả năm. Tiền góp/tháng nằm trong MilestoneModal (riêng tư chính chủ).
const BadgeCard = ({ badge, year, month, monthYear, qtyLe, qtySi, moneyLe, moneySi, onOpen }) => {
  if (!badge) return null;
  const { current, next, progress } = badge;
  const c = current.mauSac || '#06B6D4';
  const pctLabel = progress.toFixed(1).replace(/\.0$/, '');
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(160deg, #0d1f35 0%, #162840 100%)', boxShadow: `0 0 0 1.5px ${c}66, 0 8px 32px #00000060` }}>
      <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${c}, #9333ea, ${c})` }} />
      <div className="p-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">🏅 Danh hiệu của bạn</div>
        <div className="flex items-center gap-2 mb-4">
          <span style={{ fontSize: 26 }}>{current.icon || '🏆'}</span>
          <span className="text-xl font-black leading-tight" style={{ color: c, textShadow: `0 0 20px ${c}, 0 0 50px ${c}66` }}>
            {current.tenDanhHieu}
          </span>
        </div>

        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-black uppercase tracking-widest text-white/50">Hành trình {year}</span>
          <span className="text-[11px] font-black px-2 py-0.5 rounded-full" style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>
            {pctLabel}% Hoàn thành
          </span>
        </div>

        {/* Thanh tiến độ + viên kim cương ở vị trí progress% */}
        <div className="relative mt-3 mb-1" style={{ paddingTop: next && progress >= 90 ? 18 : 0 }}>
          {next && progress >= 90 && (
            <div className="absolute left-1/2 -translate-x-1/2 top-0 text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: c, color: '#0d1f35' }}>Gần đạt mốc tiếp theo!</div>
          )}
          <div className="relative h-3 rounded-full" style={{ background: '#ffffff14' }}>
            <div className="h-full rounded-full" style={{ width: `${Math.max(progress, 4)}%`, background: `linear-gradient(90deg, ${c}, #9333ea)`, boxShadow: `0 0 12px ${c}` }} />
            <div className="absolute top-1/2" style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)', fontSize: 18, filter: `drop-shadow(0 0 6px ${c})` }}>💎</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-[9px] font-bold text-white/50 mt-1">
          <span className="max-w-[45%] leading-tight">{current.icon} {current.tenDanhHieu}</span>
          <span className="max-w-[45%] text-right leading-tight" style={{ color: next ? `${c}cc` : '#FBBF24' }}>
            {next ? `${next.icon} ${next.tenDanhHieu}` : 'Đỉnh cao 🚀'}
          </span>
        </div>

        {/* 2 block RIÊNG cho đơn lẻ / đơn sỉ — mỗi loại 1 block màu riêng (teal vs tím) để phân biệt NHANH.
            Số sản phẩm theo THÁNG đang chọn (khác badge danh hiệu phía trên = lũy kế CẢ NĂM). KHÔNG tiền. */}
        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2 px-0.5">
            📦 Sản phẩm bán ra · Tháng {month}/{monthYear}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SoldBlock color="#2DD4BF" icon="🛍️" label="Đơn lẻ" value={qtyLe} money={moneyLe} />
            <SoldBlock color="#A855F7" icon="📦" label="Đơn sỉ" value={qtySi} money={moneySi} />
          </div>
        </div>

        <button onClick={onOpen}
          className="w-full mt-4 flex items-center justify-center gap-2 text-sm font-black py-2.5 rounded-xl text-white"
          style={{ background: `linear-gradient(135deg, ${c}, #9333ea)`, boxShadow: `0 4px 16px ${c}44` }}>
          <Trophy size={16} /> Xem bảng thứ hạng
        </button>
      </div>
    </div>
  );
};

// Modal chi tiết (RIÊNG TƯ chính chủ): thang 9 mốc + tiền góp từng tháng + vị trí hiện tại.
const MilestoneModal = ({ visible, onClose, badge, yearly }) => {
  if (!visible || !badge) return null;
  const { sorted, current, next, max, idx } = badge;
  const total = Number(yearly?.totalYear || 0);
  const c = current.mauSac || '#06B6D4';
  const months = yearly?.months || [];
  const monthsWithSales = months.filter((m) => Number(m.total || 0) > 0); // ẩn tháng chưa bán ra
  const maxMonth = Math.max(1, ...months.map((m) => Number(m.total || 0)));
  const remain = next && max != null ? Math.max(0, max - total) : 0;
  // Chỉ hiện current + 2 mốc kế tiếp (idx = vị trí current trong sorted tăng dần).
  // Mốc cao hơn nữa ẩn đi cho gọn — user phản hồi 9 mốc 1 lúc gây rối.
  const visibleTiers = sorted.filter((_, i) => i <= idx + 2);
  const hiddenHigher = sorted.length - visibleTiers.length;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-md max-h-[88vh] rounded-t-3xl overflow-hidden flex flex-col" style={{ background: '#0b1626' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${c}33`, background: `${c}11` }}>
          <h3 className="font-black text-base flex items-center gap-2 text-white">
            <Trophy size={18} style={{ color: c }} /> Bảng thứ hạng danh hiệu
          </h3>
          <button onClick={onClose} className="text-white/50"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {/* Thang mốc — cao nhất ở trên */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2 px-1">Thang danh hiệu {yearly?.year || ''}</div>
            <div className="space-y-1.5">
              {hiddenHigher > 0 && (
                <div className="text-center text-[10px] font-bold text-white/30 pb-0.5">
                  ⋯ còn {hiddenHigher} danh hiệu cao hơn
                </div>
              )}
              {[...visibleTiers].reverse().map((t) => {
                const achieved = total >= Number(t.doanhThuMin);
                const isCurrent = t.id === current.id;
                const tc = t.mauSac || '#64748b';
                return (
                  <div key={t.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                    style={{
                      background: isCurrent ? `${tc}22` : achieved ? '#ffffff0a' : '#ffffff05',
                      border: isCurrent ? `1.5px solid ${tc}` : `1px solid ${achieved ? tc + '44' : '#ffffff12'}`,
                      boxShadow: isCurrent ? `0 0 16px ${tc}55` : 'none',
                      opacity: achieved ? 1 : 0.55,
                    }}>
                    <span className="flex-shrink-0 flex items-center justify-center rounded-full font-black"
                      style={{
                        width: 24, height: 24, fontSize: 12,
                        background: achieved ? tc : '#ffffff14',
                        color: achieved ? '#0b1626' : '#ffffff66',
                        boxShadow: isCurrent ? `0 0 10px ${tc}` : 'none',
                      }}>{t.stt}</span>
                    <span style={{ fontSize: 22, filter: achieved ? 'none' : 'grayscale(1)' }}>{t.icon || '🏆'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black leading-tight" style={{ color: achieved ? tc : '#ffffff99' }}>{t.tenDanhHieu}</div>
                      <div className="text-[10px] text-white/40">{fmtRange(t.doanhThuMin, t.doanhThuMax)}</div>
                    </div>
                    {isCurrent
                      ? <span className="text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: tc, color: '#0b1626' }}>Bạn ở đây</span>
                      : achieved
                        ? <Check size={16} style={{ color: tc }} />
                        : <Lock size={13} className="text-white/30" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Doanh số bán ra (đơn sỉ + lẻ) mỗi tháng — RIÊNG TƯ chính chủ nên được hiện tiền.
              Chỉ hiện tháng CÓ bán ra (total > 0), ẩn tháng trống cho gọn. */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2 px-1">Doanh số bán ra mỗi tháng (sỉ + lẻ)</div>
            {monthsWithSales.length === 0 ? (
              <div className="text-center text-xs text-white/40 py-3">Chưa có doanh số bán ra trong năm {yearly?.year || ''}</div>
            ) : (
              <div className="space-y-1">
                {monthsWithSales.map((m) => {
                  const v = Number(m.total || 0);
                  const w = Math.round((v / maxMonth) * 100);
                  return (
                    <div key={m.month} className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-white/50 w-7 flex-shrink-0">T{m.month}</span>
                      <div className="flex-1 h-5 rounded-md relative overflow-hidden" style={{ background: '#ffffff0a' }}>
                        <div className="h-full rounded-md" style={{ width: `${Math.max(w, 6)}%`, background: `linear-gradient(90deg, ${c}, #9333ea)` }} />
                      </div>
                      <span className="text-[10px] font-black w-16 text-right flex-shrink-0" style={{ color: c }}>
                        {fmtMoc(v)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3.5 text-center" style={{ borderTop: `1px solid ${c}33`, background: `${c}11` }}>
          <div className="text-[11px] text-white/50 font-bold">Tổng doanh số năm {yearly?.year || ''}</div>
          <div className="text-xl font-black" style={{ color: '#FFD700', textShadow: '0 0 14px #FFD70066' }}>{fmtVNDFull(total)}</div>
          {next && remain > 0 && (
            <div className="text-[11px] font-bold mt-1" style={{ color: c }}>
              Còn {fmtMoc(remain)} nữa để đạt {next.icon} {next.tenDanhHieu}
            </div>
          )}
          {!next && <div className="text-[11px] font-bold mt-1" style={{ color: '#FBBF24' }}>🚀 Bạn đã đạt danh hiệu cao nhất!</div>}
        </div>
      </div>
    </div>
  );
};

// Thumbnail strip (horizontal scroll) — vẫn dùng cho REEL XU HƯỚNG / livestream cần caption views
const ThumbStrip = ({ items, accent, getThumb, getCaption }) => {
  if (!items.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
      {items.slice(0, 8).map((item, i) => {
        const thumb = getThumb(item);
        if (!thumb) return null;
        const caption = getCaption ? getCaption(item) : '';
        return (
          <div key={i} className="flex-shrink-0 text-center" style={{ width: 64 }}>
            <GlowPhoto src={thumb} accent={accent} className="object-cover rounded-lg" style={{ width: 64, height: 114, borderRadius: 8 }} />
            {caption && <div className="text-[9px] text-white/40 mt-0.5 line-clamp-2 leading-tight">{caption}</div>}
          </div>
        );
      })}
    </div>
  );
};

// Grid 4 cột × tối đa 2 hàng (= 8 thumb). Items shuffle theo ngày → mỗi ngày khoe khác,
// trong cùng 1 ngày thì stable. Item không có thumb hoặc URL FB hết hạn → placeholder
// gradient (xem GlowPhoto onError) để layout grid không vỡ.
const ThumbGrid = ({ items, accent, getThumb }) => {
  if (!items.length) return null;
  const picks = shuffleByDay(items).slice(0, 8);
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {picks.map((item, i) => {
        const thumb = getThumb(item);
        const placeholder = (
          <div className="flex items-center justify-center text-2xl"
            style={{ width: '100%', height: '100%', borderRadius: 10,
              background: `linear-gradient(135deg, ${accent}33, #9333ea33, ${accent}11)` }}>📸</div>
        );
        return (
          <div key={i} style={{ aspectRatio: '9/16',
            padding: 2, borderRadius: 12,
            background: `linear-gradient(135deg, ${accent}, #9333ea, ${accent}88)` }}>
            {thumb ? (
              // cdnThumb: ô chỉ ~95px nên tải bản /thumb 320px (~20-60KB) thay vì ảnh gốc
              // ~750KB — đo 31/08/2026: 24 ảnh 18,5MB → 1,0MB. lazy: khối dưới màn hình
              // (livestream, họp team) không chặn khối đầu hiện ra.
              <img src={cdnThumb(thumb)} alt="" loading="lazy" decoding="async"
                style={{ width: '100%', height: '100%', borderRadius: 10, objectFit: 'cover', display: 'block' }}
                onError={(e) => {
                  if (retryOriginalOnce(e.target)) return; // thumb hỏng → thử ảnh gốc 1 lần
                  const wrap = e.target.parentElement;
                  wrap.innerHTML = '';
                  const div = document.createElement('div');
                  div.style.cssText = `width:100%;height:100%;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:24px;background:linear-gradient(135deg,${accent}33,#9333ea33,${accent}11)`;
                  div.textContent = '📸';
                  wrap.appendChild(div);
                }} />
            ) : placeholder}
          </div>
        );
      })}
    </div>
  );
};

// Province/channel tags
const Tags = ({ items, accent }) => (
  <div className="flex flex-wrap gap-1.5 mt-2">
    {items.filter(Boolean).map((t, i) => (
      <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full"
        style={{ background: `${accent}22`, border: `1px solid ${accent}55`, color: accent }}>
        {t}
      </span>
    ))}
  </div>
);

// Thông điệp modal
const ThongDiepModal = ({ visible, onClose, accent, groups, onPick }) => {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md max-h-[85vh] rounded-t-3xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-black text-base flex items-center gap-2" style={{ color: accent }}>
            <List size={18} /> Chọn thông điệp
          </h3>
          <button onClick={onClose} className="text-gray-400"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {groups.map((g, gi) => (
            <div key={gi}>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 mb-2">{g.nhomChuDe}</div>
              <div className="space-y-1.5">
                {(g.items || []).map((it) => (
                  <button key={it.id} onClick={() => onPick(it)}
                    className="w-full text-left text-sm bg-gray-50 hover:bg-gray-100 rounded-xl px-4 py-2.5 leading-relaxed font-medium text-gray-800">
                    "{it.cauTrietLy}"
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const KhoeThanhTichThangPage = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);

  // Data states
  const [sales, setSales] = useState(null);
  const [reels, setReels] = useState([]);
  const [reelsTrending, setReelsTrending] = useState([]);
  const [livestreams, setLivestreams] = useState([]);
  const [teamMeetings, setTeamMeetings] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [spaMarkets, setSpaMarkets] = useState([]);
  const [checklistDays, setChecklistDays] = useState(0);
  const [thongDiep, setThongDiep] = useState(null);
  const [grouped, setGrouped] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Danh hiệu = doanh số lũy kế CẢ NĂM hiện tại (độc lập month picker) → load 1 lần.
  const [yearly, setYearly] = useState(null);
  const [ladder, setLadder] = useState([]);
  const [milestoneOpen, setMilestoneOpen] = useState(false);

  const accent = useMemo(() => MONTH_PALETTE[(month - 1) % 12], [month]);

  // SPEED: render từng block ngay khi API tương ứng xong, KHÔNG đợi tất cả 10 endpoint.
  // Dùng cancelKey để khi user đổi tháng nhanh thì bỏ kết quả của tháng cũ về trễ.
  const loadCancelRef = React.useRef(0);
  const loadData = useCallback(async () => {
    if (!accessToken) return;
    const myKey = ++loadCancelRef.current;
    setLoading(true);
    const stillFresh = () => loadCancelRef.current === myKey;
    const pickRows = (res) => { if (!res?.success) return []; const d = res.data; if (Array.isArray(d)) return d; return d?.rows || d?.items || []; };
    const filterMonth = (rows) => rows.filter((r) => itemInMonth(r, month, year));
    // Chỉ lấy các mục đã được công ty duyệt — đơn lẻ/sỉ không có approval nên không đụng
    const approvedOnly = (rows) => rows.filter((r) => r.companyApproved === 'approved');

    // Reset state để khung sectoin biến mất ngay khi đổi tháng (tránh hiển thị data tháng cũ)
    setSales(null); setReels([]); setReelsTrending([]); setLivestreams([]);
    setTeamMeetings([]); setMarkets([]); setSpaMarkets([]); setChecklistDays(0);

    // Fire-and-set: mỗi promise tự update state khi xong → block render dần.
    // Giữ tham chiếu salesPromise + reelsPromise để dùng lại cho Promise.allSettled (loading)
    // tránh gọi /orders/monthly-summary và /reels-auto-check thêm 1 lần nữa (đo prod 2026-05-16: duplicate +2.1s mount).
    const salesPromise = MonthlySalesAPI.getSummary(accessToken, month, year);
    salesPromise.then(r => stillFresh() && setSales(r?.success ? r.data : null));
    const reelsPromise = ReelsAutoCheckAPI.getList(accessToken, { month, year });
    reelsPromise.then(r => stillFresh() && setReels(filterMonth(approvedOnly(pickRows(r)))));
    ReelTrendingAPI.getList(accessToken, { month, year })
      .then(r => stillFresh() && setReelsTrending(filterMonth(approvedOnly(pickRows(r)))));
    LivestreamAPI.getList(accessToken, { month, year })
      .then(r => stillFresh() && setLivestreams(filterMonth(approvedOnly(pickRows(r)))));
    TeamMeetingAPI.getList(accessToken, { month, year })
      .then(r => stillFresh() && setTeamMeetings(filterMonth(approvedOnly(pickRows(r)))));
    MarketAPI.getList(accessToken, { month, year })
      .then(r => stillFresh() && setMarkets(filterMonth(approvedOnly(pickRows(r)))));
    SpaMarketAPI.getList(accessToken, { month, year })
      .then(r => stillFresh() && setSpaMarkets(filterMonth(approvedOnly(pickRows(r)))));
    DailyReportAPI.getList(accessToken, { month, year }).then(r => {
      if (!stillFresh()) return;
      const drRows = pickRows(r);
      const activeDrDays = drRows.filter((row) => {
        const d = itemDateStr(row); if (!d) return false;
        const [y, m] = d.split('-'); return Number(m) === month && Number(y) === year;
      }).length;
      setChecklistDays(activeDrDays);
    });

    // Thông điệp: load 1 lần khi vào trang, không phụ thuộc month → tách riêng useEffect bên dưới.
    // Tắt loading ngay sau khi 2 API "khung" (sales + reels) xong — đủ để user thấy nội dung.
    Promise.allSettled([salesPromise, reelsPromise])
      .then(() => stillFresh() && setLoading(false));
  }, [accessToken, month, year]);

  useEffect(() => { loadData(); }, [loadData]);

  // Thông điệp + groupedThongDiep: load 1 lần — không re-fetch khi đổi tháng.
  useEffect(() => {
    if (!accessToken) return;
    ThongDiepAPI.getRandom(accessToken).then(r => r?.success && setThongDiep(r.data));
    ThongDiepAPI.getGrouped(accessToken).then(r => r?.success && setGrouped(r.data || []));
  }, [accessToken]);

  // Danh hiệu: doanh số CẢ NĂM hiện tại + thang mốc — load 1 lần, độc lập month picker.
  useEffect(() => {
    if (!accessToken) return;
    YearlySalesAPI.getSummary(accessToken, new Date().getFullYear()).then(r => r?.success && setYearly(r.data));
    MocDanhHieuAPI.getLadder(accessToken).then(r => r?.success && setLadder(r.data || []));
  }, [accessToken]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => {
    const nm = month === 12 ? 1 : month + 1;
    const ny = month === 12 ? year + 1 : year;
    if (ny > now.getFullYear() || (ny === now.getFullYear() && nm > now.getMonth() + 1)) return;
    setMonth(nm); if (month === 12) setYear(y => y + 1);
  };

  const reloadThongDiep = async () => { const r = await ThongDiepAPI.getRandom(accessToken); if (r?.success) setThongDiep(r.data); };

  const displayName = userInfo?.facebook || userInfo?.name || userInfo?.code || 'Bạn';
  const avatarUrl = userInfo?.avatar;

  // Danh hiệu theo doanh số lũy kế cả năm (orders lẻ+sỉ). Độc lập month picker.
  const currentYear = yearly?.year || now.getFullYear();
  const badge = useMemo(() => computeBadge(ladder, Number(yearly?.totalYear || 0)), [ladder, yearly]);

  // Unique provinces/channels
  const marketProvinces = [...new Set([...markets, ...spaMarkets].map(m => m.region).filter(Boolean))];
  const liveChannels = [...new Set(livestreams.map(l => l.channel).filter(Boolean))];
  const totalLiveViews = livestreams.reduce((s, l) => s + Number(l.views || 0), 0);
  const totalReelViews = reelsTrending.reduce((s, r) => s + Number(r.viewCount || 0), 0);
  const offlineMeetings = teamMeetings.filter(m => m.meetingType?.toLowerCase().includes('offline')).length;
  const onlineMeetings = teamMeetings.length - offlineMeetings;

  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();
  const canGoNext = !isCurrentMonth;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#060d1a' }}>
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Thành tích tháng" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 w-full max-w-md mx-auto px-3 py-3 space-y-3">

        {/* ── HERO ── */}
        <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(160deg, #0d1f35 0%, #162840 100%)', boxShadow: `0 0 0 1.5px ${accent}66, 0 8px 32px #00000060` }}>
          <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${accent}, #9333ea, ${accent})` }} />
          <div className="p-4 flex gap-4 items-center">
            {/* Avatar */}
            <div className="flex-shrink-0" style={{ padding: 2.5, background: `linear-gradient(135deg, ${accent}, #9333ea)`, borderRadius: 18 }}>
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-800 flex items-center justify-center">
                {avatarUrl
                  ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  : <span className="text-3xl font-black text-white">{(displayName[0] || 'B').toUpperCase()}</span>}
              </div>
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-bold uppercase tracking-widest text-white/40 mb-0.5">Yody Phương Anh · Thành tích tháng</div>
              <div className="text-xl font-black leading-tight text-white"
                style={{ textShadow: `0 0 20px ${accent}, 0 0 50px ${accent}66` }}>
                {displayName}
              </div>
              {/* Month picker */}
              <div className="flex items-center gap-2 mt-2">
                <button onClick={prevMonth} className="rounded-lg p-1" style={{ background: `${accent}22`, border: `1px solid ${accent}44` }}>
                  <ChevronLeft size={14} color={accent} />
                </button>
                <div className="flex-1 text-center text-sm font-black" style={{ color: accent }}>
                  Tháng {month} / {year}
                </div>
                <button onClick={nextMonth} disabled={!canGoNext}
                  className="rounded-lg p-1" style={{ background: canGoNext ? `${accent}22` : '#ffffff08', border: `1px solid ${canGoNext ? accent+'44' : '#ffffff11'}` }}>
                  <ChevronRight size={14} color={canGoNext ? accent : '#ffffff30'} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── DANH HIỆU (cả năm, độc lập month picker) — thay cho khối Tổng doanh thu ── */}
        {badge && (
          <BadgeCard
            badge={badge}
            year={currentYear}
            month={month}
            monthYear={year}
            qtyLe={sales?.soLe?.totalQty || 0}
            qtySi={sales?.soSi?.totalQty || 0}
            moneyLe={sales?.soLe?.totalValue || 0}
            moneySi={sales?.soSi?.totalValue || 0}
            onOpen={() => setMilestoneOpen(true)}
          />
        )}

        {loading && (
          <div className="rounded-2xl p-8 text-center text-sm text-white/50" style={{ background: '#0d1f35' }}>
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3"
              style={{ borderColor: `${accent} ${accent} ${accent} transparent` }} />
            Đang tổng hợp thành tích tháng {month}...
          </div>
        )}

        {!loading && (<>

          {/* Khối "Tổng doanh thu tháng" cũ đã bỏ — tiền nhạy cảm. Thay bằng card Danh hiệu ở trên
              (cả năm) + tiền góp/tháng nằm trong modal "Bảng thứ hạng" (riêng tư chính chủ). */}

          {/* ── REELS — grid 4×2, shuffle theo ngày để khoe đỡ nhàm ── */}
          {reels.length > 0 && (
            <Block accent={accent} label="Reels đã đăng">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-end gap-1">
                  <span className="font-black text-white" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 20px ${accent}` }}>
                    {reels.length}
                  </span>
                  <span className="text-sm font-black mb-1" style={{ color: accent }}>reels</span>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-white/40">trong tháng {month}</div>
                </div>
              </div>
              <ThumbGrid items={reels} accent={accent} getThumb={(r) => r.thumbnailUrl} />
            </Block>
          )}

          {/* ── REEL XU HƯỚNG ── */}
          {reelsTrending.length > 0 && (
            <Block accent={accent} label="Reel xu hướng">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-end gap-1">
                    <span className="font-black text-white" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 20px ${accent}` }}>
                      {reelsTrending.length}
                    </span>
                    <span className="text-sm font-black mb-1" style={{ color: accent }}>reel</span>
                  </div>
                </div>
                {totalReelViews > 0 && (
                  <div className="text-right">
                    <div className="text-lg font-black" style={{ color: accent, textShadow: `0 0 15px ${accent}` }}>
                      {totalReelViews >= 1000 ? `${(totalReelViews / 1000).toFixed(1)}K` : totalReelViews}
                    </div>
                    <div className="text-[10px] text-white/40">tổng lượt xem</div>
                  </div>
                )}
              </div>
              <ThumbStrip items={reelsTrending} accent={accent}
                getThumb={(r) => r.proofImage}
                getCaption={(r) => r.viewCount >= 1000 ? `${(r.viewCount/1000).toFixed(0)}K` : r.viewCount ? String(r.viewCount) : ''} />
            </Block>
          )}

          {/* ── LIVESTREAM ── ảnh từ field `dateProofImage` */}
          {livestreams.length > 0 && (
            <Block accent={accent} label="Livestream">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-end gap-1">
                  <span className="font-black text-white" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 20px ${accent}` }}>
                    {livestreams.length}
                  </span>
                  <span className="text-sm font-black mb-1" style={{ color: accent }}>buổi</span>
                </div>
                {totalLiveViews > 0 && (
                  <div className="text-right">
                    <div className="text-lg font-black" style={{ color: accent }}>
                      {totalLiveViews >= 1000 ? `${(totalLiveViews / 1000).toFixed(1)}K` : totalLiveViews}
                    </div>
                    <div className="text-[10px] text-white/40">tổng lượt xem</div>
                  </div>
                )}
              </div>
              {liveChannels.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] text-white/40 mb-1">Kênh livestream</div>
                  <Tags items={liveChannels} accent={accent} />
                </div>
              )}
              {/* Livestream có 2 field ảnh: dateProofImage + productPhoto. Ưu tiên dateProof, fallback product. */}
              <ThumbGrid
                items={livestreams.flatMap(l => [l.dateProofImage, l.productPhoto]
                  .filter(p => p && p !== '').map(p => ({ thumb: p })))}
                accent={accent}
                getThumb={(it) => it.thumb}
              />
            </Block>
          )}

          {/* ── THỊ TRƯỜNG ── market dùng field `imageUrl`, spa dùng `spaImage` */}
          {(markets.length > 0 || spaMarkets.length > 0) && (
            <Block accent={accent} label="Đi thị trường">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-end gap-1">
                    <span className="font-black text-white" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 20px ${accent}` }}>
                      {markets.length + spaMarkets.length}
                    </span>
                    <span className="text-sm font-black mb-1" style={{ color: accent }}>buổi</span>
                  </div>
                  {markets.length > 0 && spaMarkets.length > 0 && (
                    <div className="text-[10px] text-white/40">{markets.length} TT thường · {spaMarkets.length} Spa</div>
                  )}
                </div>
                {(() => {
                  const totalRev = markets.reduce((s, m) => s + Number(m.revenue || 0), 0);
                  return totalRev > 0 ? (
                    <div className="text-right">
                      <div className="text-base font-black" style={{ color: accent }}>{fmtVNDShort(totalRev)}</div>
                      <div className="text-[10px] text-white/40">doanh thu</div>
                    </div>
                  ) : null;
                })()}
              </div>
              {marketProvinces.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] text-white/40 mb-1">Địa bàn</div>
                  <Tags items={marketProvinces} accent={accent} />
                </div>
              )}
              {/* Market entity có 5 ảnh: startPhoto / endPhoto / customerSupportPhoto / productSalesPhoto / appOrderPhoto.
                  Spa-market có 4: spaImage / spaProductPhoto / invoicePhoto / wholesaleOrderPhoto.
                  Legacy field `imageUrl` thường rỗng — KHÔNG dùng. Gom tất cả từ 5+4 fields, lọc giá trị thực. */}
              <ThumbGrid
                items={[
                  ...markets.flatMap(m => [m.startPhoto, m.endPhoto, m.customerSupportPhoto, m.productSalesPhoto, m.appOrderPhoto]
                    .filter(p => p && p !== '').map(p => ({ thumb: p }))),
                  ...spaMarkets.flatMap(s => [s.spaImage, s.spaProductPhoto, s.invoicePhoto, s.wholesaleOrderPhoto]
                    .filter(p => p && p !== '').map(p => ({ thumb: p }))),
                ]}
                accent={accent}
                getThumb={(it) => it.thumb}
              />
            </Block>
          )}

          {/* ── HỌP TEAM ── ảnh từ `proofImage` */}
          {teamMeetings.length > 0 && (
            <Block accent={accent} label="Hình ảnh chiến đấu cùng đồng đội">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-end gap-1">
                  <span className="font-black text-white" style={{ fontSize: 42, lineHeight: 1, textShadow: `0 0 20px ${accent}` }}>
                    {teamMeetings.length}
                  </span>
                  <span className="text-sm font-black mb-1" style={{ color: accent }}>buổi</span>
                </div>
                {(offlineMeetings > 0 || onlineMeetings > 0) && (
                  <div className="flex gap-2">
                    {offlineMeetings > 0 && (
                      <div className="rounded-xl px-3 py-1.5 text-center" style={{ background: `${accent}22`, border: `1px solid ${accent}44` }}>
                        <div className="text-lg font-black text-white">{offlineMeetings}</div>
                        <div className="text-[9px] text-white/50">Offline</div>
                      </div>
                    )}
                    {onlineMeetings > 0 && (
                      <div className="rounded-xl px-3 py-1.5 text-center" style={{ background: '#ffffff0d', border: '1px solid #ffffff1a' }}>
                        <div className="text-lg font-black text-white">{onlineMeetings}</div>
                        <div className="text-[9px] text-white/50">Online</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <ThumbGrid items={teamMeetings} accent={accent} getThumb={(m) => m.proofImage} />
            </Block>
          )}

          {/* ── THÔNG ĐIỆP ── */}
          {thongDiep && (
            <Block accent={accent} label={`Thông điệp tháng ${month} của ${displayName}`}>
              <div className="relative">
                <div className="absolute top-0 right-0 flex gap-1.5">
                  <button onClick={reloadThongDiep}
                    className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg text-white"
                    style={{ background: `${accent}44`, border: `1px solid ${accent}66` }}>
                    <Shuffle size={11} /> Đổi
                  </button>
                  <button onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg text-white"
                    style={{ background: '#ffffff11', border: '1px solid #ffffff22' }}>
                    <List size={11} /> Chọn
                  </button>
                </div>
                <div className="pt-8 pb-2 text-center">
                  <div className="text-[26px] leading-snug font-black italic text-white"
                    style={{ textShadow: `0 0 30px ${accent}60` }}>
                    "{thongDiep.cauTrietLy}"
                  </div>
                  {thongDiep.nhomChuDe && (
                    <div className="text-[10px] font-bold uppercase tracking-widest mt-3"
                      style={{ color: `${accent}99` }}>
                      — {thongDiep.nhomChuDe}
                    </div>
                  )}
                </div>
              </div>
            </Block>
          )}

          {/* Không có dữ liệu */}
          {!sales?.soLe?.count && !sales?.soSi?.count && !reels.length && !reelsTrending.length && !livestreams.length && !markets.length && !spaMarkets.length && !teamMeetings.length && (
            <div className="rounded-2xl p-8 text-center" style={{ background: '#0d1f35', border: `1px dashed ${accent}44` }}>
              <div className="text-3xl mb-2">🌱</div>
              <div className="text-sm font-bold text-white/60">Chưa có thành tích nào trong tháng {month}/{year}</div>
              <div className="text-xs text-white/30 mt-1">Hãy bắt đầu tạo dấu ấn ngay hôm nay!</div>
            </div>
          )}

          {/* ── FOOTER ── */}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d1f35, #162840)', boxShadow: `0 0 0 1px ${accent}44, 0 0 30px ${accent}22` }}>
            <div className="h-1" style={{ background: `linear-gradient(90deg, transparent, ${accent}, #9333ea, ${accent}, transparent)` }} />
            <div className="px-4 py-5 text-center">
              <div className="text-[9px] font-black uppercase tracking-widest text-white/30 mb-2">✨ Yody Phương Anh</div>
              <div className="text-xl font-black mb-1" style={{ color: accent, textShadow: `0 0 20px ${accent}` }}>
                Ghi nhận tháng {month} tuyệt vời
              </div>
              <div className="text-base font-black text-white/90" style={{ textShadow: `0 0 15px ${accent}66` }}>
                của <span style={{ color: accent }}>{displayName}</span>
              </div>
              <div className="text-xs text-white/30 mt-2 font-medium">💪 Tiếp tục tỏa sáng tháng sau!</div>
            </div>
            <div className="h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${accent}66, transparent)` }} />
          </div>

        </>)}
      </div>

      <ThongDiepModal visible={modalOpen} onClose={() => setModalOpen(false)} accent={accent} groups={grouped}
        onPick={(it) => { setThongDiep(it); setModalOpen(false); }} />

      <MilestoneModal visible={milestoneOpen} onClose={() => setMilestoneOpen(false)} badge={badge} yearly={yearly} />

      {/* Version — cập nhật mỗi khi thêm tính năng để dễ phân biệt */}
      <div className="fixed bottom-3 right-3 z-50 text-[10px] font-bold select-none pointer-events-none"
        style={{ color: accent, opacity: 0.4 }}>V26</div>
    </div>
  );
};

export default KhoeThanhTichThangPage;
